// Salon Yöneticisi — Light pastel sidebar layout.
// Admin paneli etkilenmez; sadece /salon/panel/* kullanır.
import React, { useEffect, useState, useCallback } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { venueApi, clearVenueToken } from "@/lib/venueApi";
import {
  LayoutDashboard, CalendarCheck2, Package, Wallet, BarChart3,
  MessagesSquare, LayoutGrid, Mail, Ticket, Users, ScanLine, Palette, LogOut, Menu, X, Building2, Settings,
} from "lucide-react";

const NAV = [
  { to: "/salon/panel", end: true, icon: LayoutDashboard, label: "Genel Bakış" },
  { to: "/salon/panel/rezervasyonlar", icon: CalendarCheck2, label: "Rezervasyonlar" },
  { to: "/salon/panel/paketler", icon: Package, label: "Paketler & Hizmetler" },
  { to: "/salon/panel/muhasebe", icon: Wallet, label: "Ön Muhasebe" },
  { to: "/salon/panel/doluluk", icon: BarChart3, label: "Doluluk Raporu" },
  { to: "/salon/panel/siparisler", icon: MessagesSquare, label: "Davetli Siparişleri" },
  { to: "/salon/panel/krokiler", icon: LayoutGrid, label: "Kroki & Masa Düzeni" },
  { to: "/salon/panel/davetiyeler", icon: Mail, label: "Davetiye Kodları" },
  { to: "/salon/panel/personel", icon: Users, label: "Personel & Kiosk" },
  { to: "/salon/panel/ayarlar", icon: Settings, label: "Salon Ayarları" },
  { to: "/salon/panel/branding", icon: Palette, label: "Marka & Arkaplan" },
];

export default function VenueLayout() {
  const navigate = useNavigate();
  const [me, setMe] = useState(null);
  const [branding, setBranding] = useState({ background_url: "", logo_url: "", bg_opacity: 0.08, accent_color: "#D4788E" });
  const [open, setOpen] = useState(false);

  const load = useCallback(async () => {
    try {
      const [m, b] = await Promise.all([venueApi.get("/venue/me"), venueApi.get("/venue/branding").catch(() => ({ data: { branding: {} } }))]);
      setMe(m.data);
      setBranding((prev) => ({ ...prev, ...(b.data?.branding || {}) }));
    } catch { navigate("/salon"); }
  }, [navigate]);
  useEffect(() => { load(); }, [load]);

  const logout = async () => {
    try { await venueApi.post("/venue/logout"); } catch {}
    clearVenueToken(); navigate("/salon");
  };

  const bgStyle = branding.background_url
    ? { backgroundImage: `linear-gradient(rgba(255,255,255,${1 - (branding.bg_opacity || 0.08)}),rgba(255,255,255,${1 - (branding.bg_opacity || 0.08)})), url(${branding.background_url})`, backgroundSize: "cover", backgroundPosition: "center", backgroundAttachment: "fixed" }
    : { background: "linear-gradient(135deg, #fff5f7 0%, #fef3f2 50%, #fdf4ff 100%)" };

  return (
    <div className="min-h-screen flex text-slate-800 font-sans" style={bgStyle} data-testid="venue-layout">
      {open && <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setOpen(false)} />}
      <aside className={`fixed lg:sticky top-0 left-0 z-40 w-72 h-screen bg-white/95 backdrop-blur-xl border-r border-rose-100 shadow-xl transform transition-transform ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0 flex flex-col`}>
        <div className="h-20 shrink-0 flex items-center gap-3 px-6 border-b border-rose-100 bg-gradient-to-br from-rose-50 to-pink-50">
          {branding.logo_url ? (
            <img src={branding.logo_url} alt="Logo" className="w-11 h-11 rounded-full object-cover ring-2 ring-rose-200" />
          ) : (
            <div className="w-11 h-11 rounded-full bg-gradient-to-br from-rose-400 to-pink-500 grid place-items-center text-white shadow-md"><Building2 className="w-5 h-5" /></div>
          )}
          <div className="min-w-0">
            <div className="text-base font-serif font-semibold truncate" style={{ color: branding.accent_color || "#C2185B" }}>{me?.salon_adi || "Salon"}</div>
            <div className="text-[11px] text-slate-400 tracking-wider uppercase">Düğün Yönetim Paneli</div>
          </div>
        </div>
        <nav className="flex-1 overflow-y-auto p-4 space-y-1" data-testid="venue-sidebar">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              onClick={() => setOpen(false)}
              data-testid={`venue-nav-${n.to.split("/").pop() || "home"}`}
              className={({ isActive }) => `flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${isActive ? "bg-gradient-to-r from-rose-500 to-pink-500 text-white shadow-md shadow-rose-200" : "text-slate-600 hover:bg-rose-50 hover:text-rose-700"}`}
            >
              <n.icon className="w-4 h-4" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="shrink-0 p-4 border-t border-rose-100">
          <button onClick={logout} data-testid="venue-logout" className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-xl border border-rose-200 text-slate-600 hover:bg-rose-50 text-sm font-medium transition">
            <LogOut className="w-4 h-4" /> Çıkış Yap
          </button>
        </div>
      </aside>
      <div className="flex-1 min-w-0 flex flex-col">
        <header className="lg:hidden h-14 bg-white/90 backdrop-blur border-b border-rose-100 flex items-center justify-between px-4">
          <button onClick={() => setOpen((o) => !o)} data-testid="venue-menu-toggle">{open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}</button>
          <div className="text-sm font-serif text-rose-700">{me?.salon_adi || "Salon"}</div>
          <div className="w-5" />
        </header>
        <main className="flex-1 p-5 lg:p-10 overflow-x-hidden">
          <Outlet context={{ me, branding, reloadBranding: load }} />
        </main>
      </div>
    </div>
  );
}
